type LogLevel = "error" | "warn" | "info";

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: string;
  meta?: Record<string, unknown>;
  url: string;
  userAgent: string;
  timestamp: string;
}

const LOG_BUFFER_LIMIT = 20;
const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_DEBOUNCE_MS = 2_000;

const buffer: LogEntry[] = [];
let _flushTimer: ReturnType<typeof setTimeout> | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;

function buildEntry(level: LogLevel, message: string, context?: string, meta?: Record<string, unknown>): LogEntry {
  return {
    level,
    message: message.slice(0, 2000),
    context,
    meta,
    url: typeof window !== "undefined" ? window.location.href : "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    timestamp: new Date().toISOString(),
  };
}

async function flush() {
  if (flushing || buffer.length === 0) {
    return;
  }

  const batch = buffer.splice(0, LOG_BUFFER_LIMIT);
  flushing = true;

  try {
    const runtimeConfig = useRuntimeConfig();
    const baseUrl = runtimeConfig.public.apiBaseUrl || "";
    const basePath = runtimeConfig.public.apiBasePath || "/api";

    let token: string | null = null;
    try {
      const {useSessionStore} = await import("~/stores/session");
      const sessionStore = useSessionStore();
      token = await sessionStore.getAccessToken();
    } catch {
      // Session store not ready — send without auth (will 401, that's OK)
    }

    const headers: Record<string, string> = {"Content-Type": "application/json"};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    await fetch(`${baseUrl}${basePath}/frontend-logs`, {
      method: "POST",
      headers,
      body: JSON.stringify({logs: batch}),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Silently drop — we never break the app for logging
  } finally {
    flushing = false;
  }
}

function scheduleFlush() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
}

function enqueue(entry: LogEntry) {
  buffer.push(entry);

  if (buffer.length >= LOG_BUFFER_LIMIT) {
    flush();
    return;
  }

  scheduleFlush();
}

export function useFrontendLogger() {
  const error = (message: string, context?: string, meta?: Record<string, unknown>) => {
    enqueue(buildEntry("error", message, context, meta));
  };

  const warn = (message: string, context?: string, meta?: Record<string, unknown>) => {
    enqueue(buildEntry("warn", message, context, meta));
  };

  const info = (message: string, context?: string, meta?: Record<string, unknown>) => {
    enqueue(buildEntry("info", message, context, meta));
  };

  return {error, warn, info, flush};
}

export function initFrontendLogger() {
  if (typeof window === "undefined") {
    return;
  }

  const logger = useFrontendLogger();

  window.addEventListener("error", (event) => {
    logger.error(
      event.message || "Uncaught error",
      "window.onerror",
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack?.slice(0, 1000),
      },
    );
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    logger.error(
      message.slice(0, 2000),
      "unhandledrejection",
      {stack: reason instanceof Error ? reason.stack?.slice(0, 1000) : undefined},
    );
  });

  _flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });
}
